/**
 * CreateEmployeeModal
 * Inline employee creation modal for the appointment form.
 * Collects all required fields and creates the employee without leaving the page.
 */

import React, { useEffect, useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  FormHelperText,
  Typography,
  Divider,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { CompanySelectField } from '../../../components/forms/CompanySelectField';
import employeeService from '../../../services/employeeService';
import { departmentService } from '../../../services/departmentService';
import { occupationService } from '../../../services/occupationService';
import { unitService } from '../../../services/unitService';
import { useCEPLookup } from '../../../hooks/useCEPLookup';
import { validateCPF } from '../../../utils/employeeValidations';
import { formatCPF, unformatCPF, unformatCEP } from '../../../utils/employeeFormatters';
import { BRAZILIAN_STATES } from '../../../types/common';
import {
  EmployeeGender,
  MaritalStatus,
  WorkLocation,
  GENDER_LABELS,
  MARITAL_STATUS_LABELS,
  WORK_LOCATION_LABELS,
} from '../../../types/employee';

interface CreateEmployeeModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (employee: { id: number; name: string; cpf: string; companyId: number }) => void;
  initialCpf?: string;
  initialCompanyId?: number;
}

interface SimpleOption {
  id: string | number;
  name: string;
}

interface FormData {
  companyId: string;
  workLocation: string;
  unitId: string;
  name: string;
  socialName: string;
  cpf: string;
  birthDate: string;
  gender: string;
  maritalStatus: string;
  motherName: string;
  birthplace: string;
  zipCode: string;
  state: string;
  departmentId: string;
  occupationId: string;
  admissionDate: string;
  ctps: string;
  serieCtps: string;
  ufCtps: string;
  email: string;
  whatsapp: string;
}

const schema = yup.object().shape({
  companyId: yup.string().required('Empresa é obrigatória'),
  workLocation: yup.string(),
  unitId: yup.string().when('workLocation', {
    is: (v: string) => v === WorkLocation.UNIDADE,
    then: (s) => s.required('Selecione a unidade'),
    otherwise: (s) => s,
  }),
  name: yup.string().required('Nome é obrigatório').min(3, 'Nome deve ter pelo menos 3 caracteres'),
  socialName: yup.string(),
  cpf: yup.string().required('CPF é obrigatório').test('valid-cpf', 'CPF inválido', (value) => {
    if (!value) return false;
    return validateCPF(unformatCPF(value));
  }),
  birthDate: yup.string().required('Data de nascimento é obrigatória'),
  gender: yup.string().required('Sexo é obrigatório').oneOf(['M', 'F', 'O'], 'Sexo inválido'),
  maritalStatus: yup.string(),
  motherName: yup.string().required('Nome da mãe é obrigatório'),
  birthplace: yup.string(),
  zipCode: yup.string().required('CEP é obrigatório'),
  state: yup.string().required('Estado é obrigatório'),
  departmentId: yup.string().required('Setor é obrigatório'),
  occupationId: yup.string().required('Cargo é obrigatório'),
  admissionDate: yup.string(),
  ctps: yup.string(),
  serieCtps: yup.string(),
  ufCtps: yup.string(),
  email: yup.string().email('Email inválido'),
  whatsapp: yup.string(),
});

export const CreateEmployeeModal: React.FC<CreateEmployeeModalProps> = ({
  open,
  onClose,
  onSuccess,
  initialCpf,
  initialCompanyId,
}) => {
  const [submitLoading, setSubmitLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cpfError, setCpfError] = useState<string | null>(null);
  const [departments, setDepartments] = useState<SimpleOption[]>([]);
  const [occupations, setOccupations] = useState<SimpleOption[]>([]);
  const [units, setUnits] = useState<SimpleOption[]>([]);
  const [loadingDepartments, setLoadingDepartments] = useState(false);
  const [loadingOccupations, setLoadingOccupations] = useState(false);
  const [loadingUnits, setLoadingUnits] = useState(false);

  const { lookupCEP } = useCEPLookup();

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<FormData>({
    resolver: yupResolver(schema) as any,
    defaultValues: {
      companyId: initialCompanyId ? String(initialCompanyId) : '',
      workLocation: '',
      unitId: '',
      name: '',
      socialName: '',
      cpf: initialCpf ? formatCPF(initialCpf) : '',
      birthDate: '',
      gender: '',
      maritalStatus: '',
      motherName: '',
      birthplace: '',
      zipCode: '',
      state: '',
      departmentId: '',
      occupationId: '',
      admissionDate: '',
      ctps: '',
      serieCtps: '',
      ufCtps: '',
      email: '',
      whatsapp: '',
    },
  });

  const watchCompanyId = watch('companyId');
  const watchDepartmentId = watch('departmentId');
  const watchWorkLocation = watch('workLocation');

  // Reset form when modal opens/closes
  useEffect(() => {
    if (open) {
      reset({
        companyId: initialCompanyId ? String(initialCompanyId) : '',
        name: '',
        socialName: '',
        cpf: initialCpf ? formatCPF(initialCpf) : '',
        birthDate: '',
        gender: '',
        maritalStatus: '',
        motherName: '',
        birthplace: '',
        zipCode: '',
        state: '',
        departmentId: '',
        occupationId: '',
        admissionDate: '',
        ctps: '',
        serieCtps: '',
        ufCtps: '',
        email: '',
        whatsapp: '',
      });
      setError(null);
      setCpfError(null);
      setDepartments([]);
      setOccupations([]);
      setUnits([]);
    }
  }, [open, initialCpf, initialCompanyId, reset]);

  // Load departments when company changes
  useEffect(() => {
    if (watchCompanyId) {
      setLoadingDepartments(true);
      departmentService.getDepartmentsByCompanyId(watchCompanyId)
        .then((data) => {
          const items = Array.isArray(data) ? data : [];
          setDepartments(items.map((d) => ({ id: d.id, name: d.name })));
        })
        .catch(() => setDepartments([]))
        .finally(() => setLoadingDepartments(false));
      // Reset department and occupation when company changes
      setValue('departmentId', '');
      setValue('occupationId', '');
      setOccupations([]);
    } else {
      setDepartments([]);
    }
  }, [watchCompanyId, setValue]);

  // Load occupations when department changes
  useEffect(() => {
    if (watchDepartmentId && watchCompanyId) {
      setLoadingOccupations(true);
      occupationService.getOccupations({
        limit: 100,
        filters: {
          companyId: Number(watchCompanyId),
          departmentId: Number(watchDepartmentId),
        },
      })
        .then((result) => {
          const items = Array.isArray(result.data) ? result.data : [];
          setOccupations(items.map((o) => ({ id: o.id, name: o.name })));
        })
        .catch(() => setOccupations([]))
        .finally(() => setLoadingOccupations(false));
      setValue('occupationId', '');
    } else {
      setOccupations([]);
    }
  }, [watchDepartmentId, watchCompanyId, setValue]);

  // Load units when company changes
  useEffect(() => {
    if (watchCompanyId) {
      setLoadingUnits(true);
      unitService.getUnitsByCompanyId(watchCompanyId)
        .then((data) => {
          const items = Array.isArray(data) ? data : [];
          setUnits(items.map((u) => ({ id: u.id, name: u.name })));
        })
        .catch(() => setUnits([]))
        .finally(() => setLoadingUnits(false));
      setValue('unitId', '');
    } else {
      setUnits([]);
    }
  }, [watchCompanyId, setValue]);

  // Clear unit when work location is not UNIDADE
  useEffect(() => {
    if (watchWorkLocation !== WorkLocation.UNIDADE) {
      setValue('unitId', '');
    }
  }, [watchWorkLocation, setValue]);

  // Validate CPF uniqueness on blur
  const handleCpfBlur = useCallback(async (cpfValue: string) => {
    const cleaned = unformatCPF(cpfValue);
    if (cleaned.length !== 11) return;

    const result = await employeeService.validateCPF(cleaned);
    if (!result.valid) {
      setCpfError(result.message || 'CPF já cadastrado');
    } else {
      setCpfError(null);
    }
  }, []);

  // CEP lookup
  const handleCepBlur = useCallback(async (cepValue: string) => {
    const cleaned = unformatCEP(cepValue);
    if (cleaned.length !== 8) return;

    const data = await lookupCEP(cleaned);
    if (data) {
      setValue('state', data.state);
    }
  }, [lookupCEP, setValue]);

  const handleFormSubmit = async (data: FormData) => {
    if (cpfError) return;

    setSubmitLoading(true);
    setError(null);

    try {
      const employee = await employeeService.createEmployee({
        name: data.name.trim(),
        cpf: unformatCPF(data.cpf),
        birthDate: data.birthDate,
        gender: data.gender as EmployeeGender,
        motherName: data.motherName.trim(),
        zipCode: unformatCEP(data.zipCode),
        state: data.state,
        companyId: Number(data.companyId),
        departmentId: Number(data.departmentId),
        occupationId: Number(data.occupationId),
        ...(data.workLocation ? { workLocation: data.workLocation as WorkLocation } : {}),
        ...(data.workLocation === WorkLocation.UNIDADE && data.unitId
          ? { unitId: Number(data.unitId) }
          : {}),
        ...(data.socialName ? { socialName: data.socialName.trim() } : {}),
        ...(data.maritalStatus ? { maritalStatus: data.maritalStatus as MaritalStatus } : {}),
        ...(data.birthplace ? { birthplace: data.birthplace.trim() } : {}),
        ...(data.admissionDate ? { admissionDate: data.admissionDate } : {}),
        ...(data.ctps ? { ctps: data.ctps.replace(/\D/g, '') } : {}),
        ...(data.serieCtps ? { serieCtps: data.serieCtps.trim() } : {}),
        ...(data.ufCtps ? { ufCtps: data.ufCtps } : {}),
        ...(data.email ? { email: data.email.trim() } : {}),
        ...(data.whatsapp ? { whatsapp: data.whatsapp.replace(/\D/g, '') } : {}),
      });

      onSuccess({
        id: employee.id,
        name: employee.name,
        cpf: employee.cpf,
        companyId: employee.companyId,
      });
    } catch (err: any) {
      const msg = err.response?.data?.error || 'Erro ao cadastrar funcionário';
      setError(msg);
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleClose = () => {
    if (!submitLoading) {
      onClose();
    }
  };

  // CPF mask helper
  const applyCpfMask = (value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, 11);
    if (digits.length > 9) return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`;
    if (digits.length > 6) return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6)}`;
    if (digits.length > 3) return `${digits.slice(0, 3)}.${digits.slice(3)}`;
    return digits;
  };

  // CEP mask helper
  const applyCepMask = (value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, 8);
    if (digits.length > 5) return `${digits.slice(0, 5)}-${digits.slice(5)}`;
    return digits;
  };

  // Phone mask helper
  const applyPhoneMask = (value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, 11);
    if (digits.length > 6) return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
    if (digits.length > 2) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
    return digits;
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Cadastrar Novo Funcionário</DialogTitle>
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          <Grid container spacing={2}>
            {/* === Empresa === */}
            <Grid size={12}>
              <Controller
                name="companyId"
                control={control}
                render={({ field }) => (
                  <CompanySelectField
                    value={field.value}
                    onChange={field.onChange}
                    error={!!errors.companyId}
                    helperText={errors.companyId?.message}
                    required
                    label="Empresa *"
                  />
                )}
              />
            </Grid>

            {/* === Local de Trabalho / Unidade (após a empresa) === */}
            <Grid size={{ xs: 12, md: watchWorkLocation === WorkLocation.UNIDADE ? 6 : 12 }}>
              <Controller
                name="workLocation"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth>
                    <InputLabel>Local de Trabalho</InputLabel>
                    <Select {...field} label="Local de Trabalho">
                      <MenuItem value="">Selecione</MenuItem>
                      {Object.values(WorkLocation).map((w) => (
                        <MenuItem key={w} value={w}>{WORK_LOCATION_LABELS[w]}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>
            {watchWorkLocation === WorkLocation.UNIDADE && (
              <Grid size={{ xs: 12, md: 6 }}>
                <Controller
                  name="unitId"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth error={!!errors.unitId} disabled={!watchCompanyId || loadingUnits}>
                      <InputLabel>Unidade / Posto *</InputLabel>
                      <Select {...field} label="Unidade / Posto *">
                        {units.map((u) => (
                          <MenuItem key={u.id} value={String(u.id)}>{u.name}</MenuItem>
                        ))}
                      </Select>
                      {errors.unitId && <FormHelperText>{errors.unitId.message}</FormHelperText>}
                      {loadingUnits && <FormHelperText>Carregando unidades...</FormHelperText>}
                      {!loadingUnits && watchCompanyId && units.length === 0 && (
                        <FormHelperText>Nenhuma unidade cadastrada para esta empresa</FormHelperText>
                      )}
                    </FormControl>
                  )}
                />
              </Grid>
            )}

            {/* === Dados Pessoais === */}
            <Grid size={12}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>
                Dados Pessoais
              </Typography>
              <Divider />
            </Grid>

            {/* Nome Completo | CPF */}
            <Grid size={{ xs: 12, md: 8 }}>
              <Controller
                name="name"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Nome Completo *"
                    error={!!errors.name}
                    helperText={errors.name?.message}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <Controller
                name="cpf"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="CPF *"
                    placeholder="000.000.000-00"
                    error={!!errors.cpf || !!cpfError}
                    helperText={errors.cpf?.message || cpfError || ''}
                    onChange={(e) => field.onChange(applyCpfMask(e.target.value))}
                    onBlur={() => handleCpfBlur(field.value)}
                    inputProps={{ maxLength: 14 }}
                  />
                )}
              />
            </Grid>

            {/* Nome Social | Data de Nascimento */}
            <Grid size={{ xs: 12, md: 8 }}>
              <Controller
                name="socialName"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Nome Social"
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <Controller
                name="birthDate"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Data de Nascimento *"
                    type="date"
                    error={!!errors.birthDate}
                    helperText={errors.birthDate?.message}
                    slotProps={{ inputLabel: { shrink: true } }}
                  />
                )}
              />
            </Grid>

            {/* Nome da Mãe | Naturalidade */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Controller
                name="motherName"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Nome da Mãe *"
                    error={!!errors.motherName}
                    helperText={errors.motherName?.message}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Controller
                name="birthplace"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Naturalidade"
                  />
                )}
              />
            </Grid>

            {/* Sexo | Estado Civil */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Controller
                name="gender"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth error={!!errors.gender}>
                    <InputLabel>Sexo *</InputLabel>
                    <Select {...field} label="Sexo *">
                      {Object.values(EmployeeGender).map((g) => (
                        <MenuItem key={g} value={g}>{GENDER_LABELS[g]}</MenuItem>
                      ))}
                    </Select>
                    {errors.gender && <FormHelperText>{errors.gender.message}</FormHelperText>}
                  </FormControl>
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Controller
                name="maritalStatus"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth>
                    <InputLabel>Estado Civil</InputLabel>
                    <Select {...field} label="Estado Civil">
                      <MenuItem value="">Selecione</MenuItem>
                      {Object.values(MaritalStatus).map((s) => (
                        <MenuItem key={s} value={s}>{MARITAL_STATUS_LABELS[s]}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>

            {/* === Endereço e Contato === */}
            <Grid size={12}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>
                Endereço e Contato
              </Typography>
              <Divider />
            </Grid>

            {/* CEP | Estado | Email | WhatsApp */}
            <Grid size={{ xs: 12, md: 3 }}>
              <Controller
                name="zipCode"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="CEP *"
                    placeholder="00000-000"
                    error={!!errors.zipCode}
                    helperText={errors.zipCode?.message}
                    onChange={(e) => field.onChange(applyCepMask(e.target.value))}
                    onBlur={() => handleCepBlur(field.value)}
                    inputProps={{ maxLength: 9 }}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <Controller
                name="state"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth error={!!errors.state}>
                    <InputLabel>Estado *</InputLabel>
                    <Select {...field} label="Estado *">
                      {BRAZILIAN_STATES.map((uf) => (
                        <MenuItem key={uf} value={uf}>{uf}</MenuItem>
                      ))}
                    </Select>
                    {errors.state && <FormHelperText>{errors.state.message}</FormHelperText>}
                  </FormControl>
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <Controller
                name="email"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Email"
                    type="email"
                    error={!!errors.email}
                    helperText={errors.email?.message}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <Controller
                name="whatsapp"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="WhatsApp"
                    placeholder="(00) 00000-0000"
                    onChange={(e) => field.onChange(applyPhoneMask(e.target.value))}
                    inputProps={{ maxLength: 15 }}
                  />
                )}
              />
            </Grid>

            {/* === Dados Ocupacionais === */}
            <Grid size={12}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>
                Dados Ocupacionais
              </Typography>
              <Divider />
            </Grid>

            {/* Setor | Cargo | Data de Admissão */}
            <Grid size={{ xs: 12, md: 4 }}>
              <Controller
                name="departmentId"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth error={!!errors.departmentId} disabled={!watchCompanyId || loadingDepartments}>
                    <InputLabel>Setor *</InputLabel>
                    <Select {...field} label="Setor *">
                      {departments.map((d) => (
                        <MenuItem key={d.id} value={String(d.id)}>{d.name}</MenuItem>
                      ))}
                    </Select>
                    {errors.departmentId && <FormHelperText>{errors.departmentId.message}</FormHelperText>}
                    {loadingDepartments && <FormHelperText>Carregando setores...</FormHelperText>}
                    {!watchCompanyId && <FormHelperText>Selecione uma empresa primeiro</FormHelperText>}
                  </FormControl>
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <Controller
                name="occupationId"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth error={!!errors.occupationId} disabled={!watchDepartmentId || loadingOccupations}>
                    <InputLabel>Cargo *</InputLabel>
                    <Select {...field} label="Cargo *">
                      {occupations.map((o) => (
                        <MenuItem key={o.id} value={String(o.id)}>{o.name}</MenuItem>
                      ))}
                    </Select>
                    {errors.occupationId && <FormHelperText>{errors.occupationId.message}</FormHelperText>}
                    {loadingOccupations && <FormHelperText>Carregando cargos...</FormHelperText>}
                    {!watchDepartmentId && <FormHelperText>Selecione um setor primeiro</FormHelperText>}
                  </FormControl>
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <Controller
                name="admissionDate"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Data de Admissão"
                    type="date"
                    slotProps={{ inputLabel: { shrink: true } }}
                  />
                )}
              />
            </Grid>

            {/* CTPS | Série | UF CTPS */}
            <Grid size={{ xs: 12, md: 4 }}>
              <Controller
                name="ctps"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="CTPS"
                    placeholder="0000000"
                    onChange={(e) => field.onChange(e.target.value.replace(/\D/g, '').slice(0, 7))}
                    inputProps={{ maxLength: 7 }}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <Controller
                name="serieCtps"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Série CTPS"
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <Controller
                name="ufCtps"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth>
                    <InputLabel>UF CTPS</InputLabel>
                    <Select {...field} label="UF CTPS">
                      <MenuItem value="">Selecione</MenuItem>
                      {BRAZILIAN_STATES.map((uf) => (
                        <MenuItem key={uf} value={uf}>{uf}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={submitLoading}>
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={submitLoading || !!cpfError}
          >
            {submitLoading ? <CircularProgress size={24} /> : 'Cadastrar'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
